import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Drawer, Input, Modal, Switch, Table, Button, Tag } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type {
  FilterValue,
  SorterResult,
  TableCurrentDataSource,
} from 'antd/es/table/interface';
import { SearchOutlined, SettingOutlined, VerticalAlignMiddleOutlined } from '@ant-design/icons';
import { FrontsiteOrder } from '../../../types';
import FrontsiteOrderDetails from './FrontsiteOrderDetails';

const formatDateLabel = (value?: string) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString();
};

type ColumnKey = 'orderId' | 'company' | 'salesRep' | 'total' | 'paymentStatus' | 'createdAt' | 'action';

interface ColumnConfig {
  key: ColumnKey;
  label: string;
  sortable?: boolean;
}

interface CompanyRow {
  key: string;
  company: string;
  salesRep?: string;
  ordersCount: number;
  totalAmount: number;
  latestPaymentStatus?: string;
  latestCreatedAt?: string;
  latestOrder: FrontsiteOrder | null;
  orders: FrontsiteOrder[];
}

interface StateSummaryRow {
  key: string;
  state: string;
  companyCount: number;
  orderCount: number;
  revenue: number;
  orders: FrontsiteOrder[];
}

interface ProductSummaryRow {
  key: string;
  product: string;
  sku?: string;
  companyCount: number;
  orderCount: number;
  totalQuantity: number;
  orders: FrontsiteOrder[];
}

interface FrontsiteDrawerContentProps {
  orders: FrontsiteOrder[];
  isLoading: boolean;
  formatCurrency: (value: number) => string;
  formatExactCurrency: (value: number) => string;
  onViewOrder: (order: FrontsiteOrder) => void;
}

const COLUMN_CONFIGS: ColumnConfig[] = [
  { key: 'orderId', label: 'Order ID', sortable: true },
  { key: 'company', label: 'Company', sortable: true },
  { key: 'salesRep', label: 'Sales Rep', sortable: true },
  { key: 'total', label: 'Total', sortable: true },
  { key: 'paymentStatus', label: 'Payment Status', sortable: true },
  { key: 'createdAt', label: 'Created At', sortable: true },
  { key: 'action', label: 'Action', sortable: false },
];

const COLUMN_DISPLAY_NAMES: Record<ColumnKey, string> = {
  orderId: 'Order ID',
  company: 'Company',
  salesRep: 'Sales Rep',
  total: 'Total',
  paymentStatus: 'Payment Status',
  createdAt: 'Created At',
  action: 'Action',
};

const REQUIRED_COLUMNS: ColumnKey[] = ['orderId', 'company', 'action'];

const FrontsiteDrawerContent: React.FC<FrontsiteDrawerContentProps> = ({
  orders,
  isLoading,
  formatCurrency,
  formatExactCurrency,
  onViewOrder,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [viewMode, setViewMode] = useState<'orders' | 'companies'>('orders');
  const [tablePagination, setTablePagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
  });
  const [orderSortState, setOrderSortState] = useState<{ key?: ColumnKey; order?: 'ascend' | 'descend' }>({});
  const [companySortState, setCompanySortState] = useState<{ key?: string; order?: 'ascend' | 'descend' }>({});
  const [columnVisibility, setColumnVisibility] = useState<Record<ColumnKey, boolean>>({
    orderId: true,
    company: true,
    salesRep: true,
    total: true,
    paymentStatus: true,
    createdAt: true,
    action: true,
  });
  const [columnOrder, setColumnOrder] = useState<ColumnKey[]>(() => COLUMN_CONFIGS.map((column) => column.key));
  const [columnSettingsVisible, setColumnSettingsVisible] = useState(false);
  const [draggedColumn, setDraggedColumn] = useState<ColumnKey | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<ColumnKey | null>(null);
  const [stateDrawerVisible, setStateDrawerVisible] = useState(false);
  const [productDrawerVisible, setProductDrawerVisible] = useState(false);
  const [orderListDrawerState, setOrderListDrawerState] = useState<{
    open: boolean;
    title: string;
    orders: FrontsiteOrder[];
    selectedOrder: FrontsiteOrder | null;
  }>({ open: false, title: 'Orders', orders: [], selectedOrder: null });
  const [orderListPagination, setOrderListPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
  });
  const [orderListSearchValue, setOrderListSearchValue] = useState('');
  const [orderListSortState, setOrderListSortState] = useState<{ key?: string; order?: 'ascend' | 'descend' }>({});

  useEffect(() => {
    setTablePagination((prev) => ({ ...prev, current: 1 }));
  }, [searchValue, orders, viewMode]);

  useEffect(() => {
    if (orderListDrawerState.open) {
      setOrderListPagination((prev) => ({ ...prev, current: 1 }));
    }
  }, [orderListDrawerState.open]);

  useEffect(() => {
    setOrderListPagination((prev) => ({ ...prev, current: 1 }));
  }, [orderListSearchValue, orderListDrawerState.orders]);

  const filteredOrders = useMemo(() => {
    if (!orders || orders.length === 0) return [];
    const normalizedSearch = searchValue.trim().toLowerCase();
    if (!normalizedSearch) {
      return orders;
    }
    return orders.filter((order) => {
      const pool = [
        order.company,
        order.so_number,
        order.order_id ? String(order.order_id) : '',
        order.sales_representative,
        order.payment_status,
      ]
        .filter(Boolean)
        .map((value) => String(value).toLowerCase());
      return pool.some((value) => value.includes(normalizedSearch));
    });
  }, [orders, searchValue]);

  const filteredOrderList = useMemo(() => {
    if (!orderListDrawerState.orders || orderListDrawerState.orders.length === 0) return [];
    const normalizedSearch = orderListSearchValue.trim().toLowerCase();
    if (!normalizedSearch) {
      return orderListDrawerState.orders;
    }
    return orderListDrawerState.orders.filter((order) => {
      const pool = [
        order.company,
        order.so_number,
        order.order_id ? String(order.order_id) : '',
      ]
        .filter(Boolean)
        .map((value) => String(value).toLowerCase());
      return pool.some((value) => value.includes(normalizedSearch));
    });
  }, [orderListDrawerState.orders, orderListSearchValue]);

  const analytics = useMemo(() => {
    const uniqueCompanies = new Set<string>();
    let completedOrders = 0;
    let totalRevenue = 0;
    filteredOrders.forEach((order) => {
      if (order.company) uniqueCompanies.add(order.company);
      if ((order.payment_status || '').toLowerCase() === 'completed') completedOrders += 1;
      totalRevenue += Number(order.total) || 0;
    });
    return {
      uniqueCompanies: uniqueCompanies.size,
      totalRecords: filteredOrders.length,
      completedOrders,
      totalRevenue,
    };
  }, [filteredOrders]);

  const orderedColumns = useMemo(
    () =>
      columnOrder
        .map((key) => COLUMN_CONFIGS.find((column) => column.key === key))
        .filter((column): column is ColumnConfig => Boolean(column)),
    [columnOrder],
  );

  const sortedColumns = useMemo(
    () => columnOrder.map((key) => [key, columnVisibility[key] ?? true] as [ColumnKey, boolean]),
    [columnOrder, columnVisibility],
  );

  const handleOpenColumnSettings = () => {
    setColumnSettingsVisible(true);
  };

  const handleCloseColumnSettings = () => {
    setColumnSettingsVisible(false);
    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  const handleColumnToggle = (key: ColumnKey) => {
    if (REQUIRED_COLUMNS.includes(key)) return;
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleResetColumns = () => {
    setColumnOrder(COLUMN_CONFIGS.map((column) => column.key));
    setColumnVisibility({
      orderId: true,
      company: true,
      salesRep: true,
      total: true,
      paymentStatus: true,
      createdAt: true,
      action: true,
    });
  };

  const handleDragStart = (event: React.DragEvent<HTMLDivElement>, key: ColumnKey) => {
    event.dataTransfer?.setData('text/plain', key);
    setDraggedColumn(key);
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>, key: ColumnKey) => {
    event.preventDefault();
    if (key !== draggedColumn) {
      setDragOverColumn(key);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>, key: ColumnKey) => {
    event.preventDefault();
    if (!draggedColumn || draggedColumn === key) {
      return;
    }
    const newOrder = [...columnOrder];
    const fromIndex = newOrder.indexOf(draggedColumn);
    const toIndex = newOrder.indexOf(key);
    if (fromIndex === -1 || toIndex === -1) {
      return;
    }
    newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, draggedColumn);
    setColumnOrder(newOrder);
    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  const handleOpenOrderListDrawer = useCallback((title: string, ordersToShow: FrontsiteOrder[]) => {
    setOrderListDrawerState({
      open: true,
      title,
      orders: ordersToShow,
      selectedOrder: null,
    });
  }, []);

  const handleOrderListTableChange = (
    pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<FrontsiteOrder> | SorterResult<FrontsiteOrder>[],
  ) => {
    setOrderListPagination((prev) => ({
      current: pagination.current || prev.current,
      pageSize: pagination.pageSize || prev.pageSize,
    }));
    const normalizedSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    if (normalizedSorter?.order && normalizedSorter.columnKey) {
      setOrderListSortState({
        key: String(normalizedSorter.columnKey),
        order: normalizedSorter.order,
      });
    } else {
      setOrderListSortState({});
    }
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter:
      | SorterResult<FrontsiteOrder | CompanyRow>
      | SorterResult<FrontsiteOrder | CompanyRow>[],
    _extra: TableCurrentDataSource<FrontsiteOrder | CompanyRow>,
  ) => {
    setTablePagination((prev) => ({
      current: pagination.current || prev.current,
      pageSize: pagination.pageSize || prev.pageSize,
    }));
    const normalizedSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    if (viewMode === 'orders') {
      if (normalizedSorter?.order && normalizedSorter.columnKey) {
        setOrderSortState({
          key: normalizedSorter.columnKey as ColumnKey,
          order: normalizedSorter.order,
        });
      } else {
        setOrderSortState({});
      }
    } else {
      if (normalizedSorter?.order && normalizedSorter.columnKey) {
        setCompanySortState({
          key: String(normalizedSorter.columnKey),
          order: normalizedSorter.order,
        });
      } else {
        setCompanySortState({});
      }
    }
  };

  const companyRows = useMemo<CompanyRow[]>(() => {
    const map = new Map<string, CompanyRow & { latestTimestamp: number }>();
    filteredOrders.forEach((order) => {
      const companyKey = (order.company || `company-${order.userid || order.order_id || order.id || 'na'}`)
        .toString()
        .toLowerCase();
      const createdTimestamp = order.created_at ? new Date(order.created_at).getTime() : 0;
      const existing = map.get(companyKey);
      const totalAmount = Number(order.total) || 0;
      if (!existing) {
        map.set(companyKey, {
          key: companyKey,
          company: order.company || 'Unknown Company',
          salesRep: order.sales_representative || 'N/A',
          ordersCount: 1,
          totalAmount,
          latestPaymentStatus: order.payment_status || undefined,
          latestCreatedAt: order.created_at,
          latestOrder: order,
          latestTimestamp: createdTimestamp,
          orders: [order],
        });
      } else {
        existing.ordersCount += 1;
        existing.totalAmount += totalAmount;
        existing.orders.push(order);
        if (createdTimestamp >= existing.latestTimestamp) {
          existing.latestPaymentStatus = order.payment_status || existing.latestPaymentStatus;
          existing.latestCreatedAt = order.created_at || existing.latestCreatedAt;
          existing.salesRep = order.sales_representative || existing.salesRep;
          existing.latestOrder = order;
          existing.latestTimestamp = createdTimestamp;
        }
      }
    });
    return Array.from(map.values())
      .sort((a, b) => (b.latestTimestamp || 0) - (a.latestTimestamp || 0))
      .map(({ latestTimestamp, ...rest }) => rest);
  }, [filteredOrders]);

  const statesSummary = useMemo<StateSummaryRow[]>(() => {
    const map = new Map<string, { state: string; companySet: Set<string>; orders: FrontsiteOrder[]; revenue: number }>();
    orders.forEach((order) => {
      const rawState = typeof order.state === 'string' && order.state.trim().length > 0 ? order.state.trim() : 'Unknown State';
      const key = rawState.toLowerCase();
      const entry = map.get(key) ?? {
        state: rawState,
        companySet: new Set<string>(),
        orders: [],
        revenue: 0,
      };
      entry.orders.push(order);
      entry.revenue += Number(order.total) || 0;
      if (order.company) {
        entry.companySet.add(order.company);
      }
      map.set(key, entry);
    });
    return Array.from(map.entries()).map(([key, entry]) => ({
      key,
      state: entry.state,
      companyCount: entry.companySet.size,
      orderCount: entry.orders.length,
      revenue: entry.revenue,
      orders: entry.orders,
    }));
  }, [orders]);

  const productsSummary = useMemo<ProductSummaryRow[]>(() => {
    const map = new Map<
      string,
      {
        product: string;
        sku?: string;
        orders: Map<string | number, FrontsiteOrder>;
        companySet: Set<string>;
        totalQuantity: number;
      }
    >();

    orders.forEach((order) => {
      if (!Array.isArray(order.items)) {
        return;
      }
      order.items.forEach((item, index) => {
        const productLabel =
          (typeof item?.name === 'string' && item.name.trim().length > 0 ? item.name.trim() : undefined) ||
          'Unknown Product';
        const skuLabel = item?.sku || undefined;
        const keySource = skuLabel || productLabel || `product-${index}`;
        const key = keySource.toLowerCase();
        const entry =
          map.get(key) ??
          {
            product: productLabel,
            sku: skuLabel,
            orders: new Map<string | number, FrontsiteOrder>(),
            companySet: new Set<string>(),
            totalQuantity: 0,
          };
        const orderKey = order.order_id ?? order.id ?? `${order.company || 'order'}-${entry.orders.size}`;
        if (!entry.orders.has(orderKey)) {
          entry.orders.set(orderKey, order);
        }
        if (order.company) {
          entry.companySet.add(order.company);
        }
        entry.totalQuantity += Number(item?.qty) || 0;
        map.set(key, entry);
      });
    });

    return Array.from(map.entries()).map(([key, entry]) => ({
      key,
      product: entry.product,
      sku: entry.sku,
      companyCount: entry.companySet.size,
      orderCount: entry.orders.size,
      totalQuantity: entry.totalQuantity,
      orders: Array.from(entry.orders.values()),
    }));
  }, [orders]);

  const orderColumns = useMemo<ColumnsType<FrontsiteOrder>>(() => {
    const compareString = (a?: string | number, b?: string | number) => {
      const left = (a ?? '').toString().toLowerCase();
      const right = (b ?? '').toString().toLowerCase();
      if (left < right) return -1;
      if (left > right) return 1;
      return 0;
    };

    return orderedColumns
      .filter((column) => columnVisibility[column.key])
      .map((column) => {
        const sortOrderValue = orderSortState.key === column.key ? orderSortState.order : undefined;
        switch (column.key) {
          case 'orderId':
            return {
              key: column.key,
              dataIndex: 'so_number',
              title: column.label,
              sorter: (a, b) => {
                const left = a.so_number || `Order #${a.order_id || a.id || ''}`;
                const right = b.so_number || `Order #${b.order_id || b.id || ''}`;
                return compareString(left, right);
              },
              render: (_: unknown, record: FrontsiteOrder) => (
                <span style={{ fontWeight: 600, color: '#0f172a' }}>
                  {record.so_number || `Order #${record.order_id || record.id}`}
                </span>
              ),
              sortOrder: sortOrderValue,
            };
          case 'company':
            return {
              key: column.key,
              dataIndex: 'company',
              title: column.label,
              sorter: (a, b) => compareString(a.company, b.company),
              render: (_: unknown, record: FrontsiteOrder) => (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontWeight: 600 }}>{record.company || 'N/A'}</span>
                  <span style={{ fontSize: 12, color: '#94a3b8' }}>
                    {record.city ? `${record.city}${record.state ? `, ${record.state}` : ''}` : 'Location unavailable'}
                  </span>
                </div>
              ),
              sortOrder: sortOrderValue,
            };
          case 'salesRep':
            return {
              key: column.key,
              dataIndex: 'sales_representative',
              title: column.label,
              sorter: (a, b) => compareString(a.sales_representative, b.sales_representative),
              render: (value: FrontsiteOrder['sales_representative']) => value || 'N/A',
              sortOrder: sortOrderValue,
            };
          case 'total':
            return {
              key: column.key,
              dataIndex: 'total',
              title: column.label,
              align: 'right' as const,
              sorter: (a, b) => (Number(a.total) || 0) - (Number(b.total) || 0),
              render: (value: FrontsiteOrder['total']) => (
                <span style={{ fontWeight: 600, color: '#0b8043' }}>{formatCurrency(Number(value) || 0)}</span>
              ),
              sortOrder: sortOrderValue,
            };
          case 'paymentStatus':
            return {
              key: column.key,
              dataIndex: 'payment_status',
              title: column.label,
              sorter: (a, b) => compareString(a.payment_status, b.payment_status),
              render: (value: FrontsiteOrder['payment_status']) => (
                <Tag
                  style={{
                    backgroundColor: (value || '').toLowerCase() === 'completed' ? '#e6f4ea' : '#fef3c7',
                    color: (value || '').toLowerCase() === 'completed' ? '#0b8043' : '#b45309',
                    fontWeight: 600,
                  }}
                >
                  {value ? value.toUpperCase() : 'N/A'}
                </Tag>
              ),
              sortOrder: sortOrderValue,
            };
          case 'createdAt':
            return {
              key: column.key,
              dataIndex: 'created_at',
              title: column.label,
              sorter: (a, b) => {
                const left = a.created_at ? new Date(a.created_at).getTime() : 0;
                const right = b.created_at ? new Date(b.created_at).getTime() : 0;
                return left - right;
              },
              defaultSortOrder: 'descend' as const,
              render: (value: FrontsiteOrder['created_at']) => formatDateLabel(value),
              sortOrder: sortOrderValue,
            };
          case 'action':
          default:
            return {
              key: column.key,
              dataIndex: 'action',
              title: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  Actions
                  <Button type="text" size="small" icon={<SettingOutlined />} onClick={handleOpenColumnSettings} />
                </div>
              ),
              render: (_: unknown, record: FrontsiteOrder) => (
                <Button type="primary" size="small" onClick={() => onViewOrder(record)}>
                  View
                </Button>
              ),
              sorter: false,
            };
        }
      });
  }, [orderedColumns, columnVisibility, handleOpenColumnSettings, onViewOrder, formatCurrency, orderSortState]);

  const companyColumns = useMemo<ColumnsType<CompanyRow>>(
    () => [
      {
        key: 'company',
        dataIndex: 'company',
        title: 'Company',
        sorter: (a, b) => a.company.localeCompare(b.company),
        render: (value: string) => <span style={{ fontWeight: 600 }}>{value}</span>,
        sortOrder: companySortState.key === 'company' ? companySortState.order : undefined,
      },
      {
        key: 'salesRep',
        dataIndex: 'salesRep',
        title: 'Sales Rep',
        sorter: (a, b) => (a.salesRep || '').localeCompare(b.salesRep || ''),
        render: (value?: string) => value || 'N/A',
        sortOrder: companySortState.key === 'salesRep' ? companySortState.order : undefined,
      },
      {
        key: 'ordersCount',
        dataIndex: 'ordersCount',
        title: 'Orders',
        align: 'right' as const,
        sorter: (a, b) => a.ordersCount - b.ordersCount,
        render: (value: number, record: CompanyRow) => (
          <Button
            type="text"
            size="small"
            onClick={() => handleOpenOrderListDrawer(`${record.company || 'Company'} Orders`, record.orders)}
          >
            {value.toLocaleString()}
          </Button>
        ),
        sortOrder: companySortState.key === 'ordersCount' ? companySortState.order : undefined,
      },
      {
        key: 'totalAmount',
        dataIndex: 'totalAmount',
        title: 'Total',
        align: 'right' as const,
        sorter: (a, b) => a.totalAmount - b.totalAmount,
        render: (value: number) => formatCurrency(value),
        sortOrder: companySortState.key === 'totalAmount' ? companySortState.order : undefined,
      },
      {
        key: 'latestPaymentStatus',
        dataIndex: 'latestPaymentStatus',
        title: 'Payment Status',
        render: (value?: string) => (
          <Tag
            style={{
              backgroundColor: (value || '').toLowerCase() === 'completed' ? '#e6f4ea' : '#fef3c7',
              color: (value || '').toLowerCase() === 'completed' ? '#0b8043' : '#b45309',
              fontWeight: 600,
            }}
          >
            {value ? value.toUpperCase() : 'N/A'}
          </Tag>
        ),
        sorter: (a, b) => {
          const left = (a.latestPaymentStatus || '').toLowerCase();
          const right = (b.latestPaymentStatus || '').toLowerCase();
          if (left < right) return -1;
          if (left > right) return 1;
          return 0;
        },
        sortOrder: companySortState.key === 'latestPaymentStatus' ? companySortState.order : undefined,
      },
      {
        key: 'latestCreatedAt',
        dataIndex: 'latestCreatedAt',
        title: 'Latest Order',
        sorter: (a, b) => {
          const left = a.latestCreatedAt ? new Date(a.latestCreatedAt).getTime() : 0;
          const right = b.latestCreatedAt ? new Date(b.latestCreatedAt).getTime() : 0;
          return left - right;
        },
        defaultSortOrder: 'descend',
        render: (value?: string) => formatDateLabel(value),
        sortOrder: companySortState.key === 'latestCreatedAt' ? companySortState.order : undefined,
      },
      {
        key: 'action',
        dataIndex: 'action',
        title: 'Action',
        render: (_: unknown, record: CompanyRow) => (
          <Button
            type="primary"
            size="small"
            disabled={!record.latestOrder}
            onClick={() => handleOpenOrderListDrawer(`${record.company || 'Company'} Orders`, record.orders)}
          >
            View
          </Button>
        ),
      },
    ],
    [formatCurrency, handleOpenOrderListDrawer, companySortState],
  );

  const orderListColumns = useMemo<ColumnsType<FrontsiteOrder>>(() => [
    {
      title: 'Order ID',
      dataIndex: 'so_number',
      key: 'listOrderId',
      sorter: (a, b) => {
        const left = a.so_number || `Order #${a.order_id || a.id || ''}`;
        const right = b.so_number || `Order #${b.order_id || b.id || ''}`;
        return left.localeCompare(right);
      },
      sortOrder: orderListSortState.key === 'listOrderId' ? orderListSortState.order : undefined,
      render: (_: unknown, record) => <span className="font-semibold">{record.so_number || `Order #${record.order_id || record.id}`}</span>,
    },
    {
      title: 'Company',
      dataIndex: 'company',
      key: 'listCompany',
      sorter: (a, b) => (a.company || '').localeCompare(b.company || ''),
      sortOrder: orderListSortState.key === 'listCompany' ? orderListSortState.order : undefined,
      render: (value: string | undefined) => value || 'N/A',
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'listCreated',
      sorter: (a, b) => {
        const left = a.created_at ? new Date(a.created_at).getTime() : 0;
        const right = b.created_at ? new Date(b.created_at).getTime() : 0;
        return left - right;
      },
      sortOrder: orderListSortState.key === 'listCreated' ? orderListSortState.order : undefined,
      render: (value: string | undefined) => formatDateLabel(value),
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'listTotal',
      align: 'right',
      sorter: (a, b) => (Number(a.total) || 0) - (Number(b.total) || 0),
      sortOrder: orderListSortState.key === 'listTotal' ? orderListSortState.order : undefined,
      render: (value: number | undefined) => formatCurrency(value ?? 0),
    },
    {
      title: 'Status',
      dataIndex: 'payment_status',
      key: 'listStatus',
      sorter: (a, b) => (a.payment_status || '').localeCompare(b.payment_status || ''),
      sortOrder: orderListSortState.key === 'listStatus' ? orderListSortState.order : undefined,
      render: (value: string | undefined) => (
        <Tag
          style={{
            backgroundColor: (value || '').toLowerCase() === 'completed' ? '#e6f4ea' : '#fef3c7',
            color: (value || '').toLowerCase() === 'completed' ? '#0b8043' : '#b45309',
            fontWeight: 600,
          }}
        >
          {value ? value.toUpperCase() : 'N/A'}
        </Tag>
      ),
    },
    {
      title: 'Action',
      key: 'listAction',
      render: (_: unknown, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() =>
            setOrderListDrawerState((prev) => ({
              ...prev,
              selectedOrder: record,
            }))
          }
        >
          View
        </Button>
      ),
    },
  ], [formatCurrency, orderListSortState]);

  const tableData = viewMode === 'orders' ? filteredOrders : companyRows;
  const columns = viewMode === 'orders' ? orderColumns : companyColumns;

  const cards = [
    {
      key: 'unique',
      label: 'Unique Companies',
      value: analytics.uniqueCompanies.toLocaleString(),
      color: '#1d4ed8',
      background: '#e8f0fe',
      onClick: () => setViewMode('companies'),
      isActive: viewMode === 'companies',
    },
    {
      key: 'records',
      label: 'Total Records',
      value: analytics.totalRecords.toLocaleString(),
      color: '#0f172a',
      background: '#fef3c7',
      onClick: () => setViewMode('orders'),
      isActive: viewMode === 'orders',
    },
    {
      key: 'completed',
      label: 'Completed Orders',
      value: analytics.completedOrders.toLocaleString(),
      color: '#059669',
      background: '#dcfce7',
      onClick: () => setViewMode('orders'),
      isActive: viewMode === 'orders',
    },
    {
      key: 'revenue',
      label: 'Total Revenue',
      value: formatCurrency(analytics.totalRevenue),
      color: '#b45309',
      background: '#fff7e6',
      onClick: () => setViewMode('orders'),
      isActive: viewMode === 'orders',
    },
    {
      key: 'states',
      label: 'Unique States',
      value: statesSummary.length.toLocaleString(),
      color: '#0d9488',
      background: '#ccfbf1',
      onClick: () => setStateDrawerVisible(true),
      isActive: stateDrawerVisible,
    },
    {
      key: 'products',
      label: 'Unique Products',
      value: productsSummary.length.toLocaleString(),
      color: '#9333ea',
      background: '#ede9fe',
      onClick: () => setProductDrawerVisible(true),
      isActive: productDrawerVisible,
    },
  ];

  return (

    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 8,
        }}
      >
        {cards.map((card) => (
          <div
            key={card.key}
            onClick={card.onClick}
            style={{
              padding: 10,
              borderRadius: 4,
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              background: card.background,
              cursor: 'pointer',
              border: card.isActive ? '2px solid rgba(15,23,42,0.2)' : '2px solid transparent',
              transition: 'border-color 0.2s ease',
            }}
          >
            <div style={{ fontSize: 12, color: card.color, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              {card.label}
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: card.color }}>
              {card.value}
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: 16,
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
      >
        <Input
          placeholder="Search by order #, company, sales rep..."
          allowClear
          value={searchValue}
          onChange={(event) => setSearchValue(event.target.value)}
          size="large"
          suffix={<SearchOutlined className="text-lg !text-blue-600" />}
          className="w-full sm:!w-96 !rounded"
        />
        <span style={{ color: '#475569', fontWeight: 600, fontSize: 14 }}>
          View: {viewMode === 'companies' ? 'Unique Companies' : 'Orders'}
        </span>
      </div>

      <Table<FrontsiteOrder | CompanyRow>
        columns={columns as ColumnsType<any>}
        dataSource={tableData}
        rowKey={(record, index) =>
          viewMode === 'orders'
            ? `order-${index}`
            : (record as CompanyRow).key
        }
        loading={isLoading}
        pagination={{
          current: tablePagination.current || 1,
          pageSize: tablePagination.pageSize || 10,
          total: tableData.length,
          showSizeChanger: true,
          pageSizeOptions: ['10', '25', '50'],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} entries`,
          className: '!px-4'
        }}
        scroll={{ x: 'max-content' }}
        locale={{
          emptyText: isLoading
            ? 'Loading orders...'
            : viewMode === 'companies' && companyRows.length === 0
            ? 'No companies available for the selected filters.'
            : filteredOrders.length === 0
            ? searchValue
              ? `No orders match "${searchValue}".`
              : 'No orders available for the current filters.'
            : 'No data',
        }}
        onChange={handleTableChange}
        size='small'
        className='border'
      />

      <Modal
        title="Column Settings"
        open={columnSettingsVisible}
        onCancel={handleCloseColumnSettings}
        footer={[
          <Button key="reset" onClick={handleResetColumns}>
            Reset to Default
          </Button>,
          <Button key="close" type="primary" onClick={handleCloseColumnSettings}>
            Close
          </Button>,
        ]}
        bodyStyle={{
          maxHeight: '60vh',
          overflow: 'hidden',
          padding: 0,
        }}
        width={600}
      >
        <div
          className="pe-2 py-4 column-settings-modal-content"
          style={{
            maxHeight: 'calc(60vh - 40px)',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          <div className="mb-4 text-sm text-gray-600">
            Drag and drop to reorder columns. Toggle visibility with the switches.
          </div>
          <div className="space-y-2" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {sortedColumns.map(([key, value]) => (
              <div
                key={key}
                draggable
                onDragStart={(event) => handleDragStart(event, key)}
                onDragEnter={(event) => handleDragEnter(event, key)}
                onDragOver={handleDragOver}
                onDrop={(event) => handleDrop(event, key)}
                onDragEnd={handleDragEnd}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  backgroundColor: dragOverColumn === key ? '#f8fafc' : '#fff',
                  opacity: draggedColumn === key ? 0.6 : 1,
                  cursor: 'move',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <VerticalAlignMiddleOutlined style={{ color: '#2563eb' }} />
                  <span style={{ fontWeight: 600 }}>{COLUMN_DISPLAY_NAMES[key]}</span>
                </div>
                <Switch
                  size="small"
                  checked={value}
                  disabled={REQUIRED_COLUMNS.includes(key)}
                  onChange={() => handleColumnToggle(key)}
                />
              </div>
            ))}
          </div>
        </div>
      </Modal>

      <Drawer
        title="State Insights"
        placement="right"
        width={860}
        open={stateDrawerVisible}
        onClose={() => setStateDrawerVisible(false)}
        destroyOnClose
      >
        <Table<StateSummaryRow>
          columns={[
            {
              title: 'State',
              dataIndex: 'state',
              key: 'state',
              sorter: (a, b) => a.state.localeCompare(b.state),
            },
            {
              title: 'Companies',
              dataIndex: 'companyCount',
              key: 'companyCount',
              align: 'right',
              sorter: (a, b) => a.companyCount - b.companyCount,
            },
            {
              title: 'Orders',
              dataIndex: 'orderCount',
              key: 'orderCount',
              align: 'right',
              sorter: (a, b) => a.orderCount - b.orderCount,
              render: (value: number, record) => (
                <Button
                  type="text"
                  size="small"
                  onClick={() => handleOpenOrderListDrawer(`${record.state} Orders`, record.orders)}
                >
                  {value.toLocaleString()}
                </Button>
              ),
            },
            {
              title: 'Revenue',
              dataIndex: 'revenue',
              key: 'revenue',
              align: 'right',
              sorter: (a, b) => a.revenue - b.revenue,
              render: (value: number) => formatCurrency(value),
            },
            {
              title: 'Action',
              key: 'stateAction',
              render: (_: unknown, record) => (
                <Button
                  type="primary"
                  size="small"
                  onClick={() => handleOpenOrderListDrawer(`${record.state} Orders`, record.orders)}
                >
                  View
                </Button>
              ),
            },
          ]}
          dataSource={statesSummary}
          rowKey={(record) => record.key}
          pagination={{ pageSize: 8 }}
          size="small"
        />
      </Drawer>

      <Drawer
        title="Product Insights"
        placement="right"
        width={860}
        open={productDrawerVisible}
        onClose={() => setProductDrawerVisible(false)}
        destroyOnClose
      >
        <Table<ProductSummaryRow>
          columns={[
            {
              title: 'Product',
              dataIndex: 'product',
              key: 'product',
              sorter: (a, b) => a.product.localeCompare(b.product),
            },
            {
              title: 'SKU',
              dataIndex: 'sku',
              key: 'sku',
              render: (value?: string) => value || 'N/A',
            },
            {
              title: 'Companies',
              dataIndex: 'companyCount',
              key: 'productCompanies',
              align: 'right',
              sorter: (a, b) => a.companyCount - b.companyCount,
            },
            {
              title: 'Orders',
              dataIndex: 'orderCount',
              key: 'productOrders',
              align: 'right',
              sorter: (a, b) => a.orderCount - b.orderCount,
              render: (value: number, record) => (
                <Button
                  type="text"
                  size="small"
                  onClick={() => handleOpenOrderListDrawer(`${record.product} Orders`, record.orders)}
                >
                  {value.toLocaleString()}
                </Button>
              ),
            },
            {
              title: 'Total Qty',
              dataIndex: 'totalQuantity',
              key: 'totalQuantity',
              align: 'right',
              sorter: (a, b) => a.totalQuantity - b.totalQuantity,
              render: (value: number) => value.toLocaleString(),
            },
            {
              title: 'Action',
              key: 'productAction',
              render: (_: unknown, record) => (
                <Button
                  type="primary"
                  size="small"
                  onClick={() => handleOpenOrderListDrawer(`${record.product} Orders`, record.orders)}
                >
                  View
                </Button>
              ),
            },
          ]}
          dataSource={productsSummary}
          rowKey={(record) => record.key}
          pagination={{ pageSize: 8 }}
          size="small"
        />
      </Drawer>

{/* unique company view > view drawer */}
      <Drawer
        title={orderListDrawerState.title}
        placement="right"
        width={1150}
        open={orderListDrawerState.open}
        onClose={() =>
          setOrderListDrawerState({
            open: false,
            title: 'Orders',
            orders: [],
            selectedOrder: null,
          })
        }
        push={false}
        destroyOnClose
      >
        {filteredOrderList.length === 0 ? (
          <div style={{ padding: 16, color: '#94a3b8' }}>No orders were found for this selection.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded p-3 bg-blue-50">
                <span className="text-blue-400 uppercase tracking-wider text-xs block">
                  Orders
                </span>
                <span className="text-blue-700 font-bold text-xl">
                  {filteredOrderList.length.toLocaleString()}
                </span>
              </div>
              <div className="rounded p-3 bg-orange-50">
                <span className="text-orange-400 uppercase tracking-wider text-xs block">
                  Total Amount
                </span>
                <span className="text-orange-400 font-bold text-xl">
                  {formatCurrency(
                    filteredOrderList.reduce((sum, order) => sum + (Number(order.total) || 0), 0),
                  )}
                </span>
              </div>
            </div>
            <Input
              placeholder="Search by order #, company..."
              allowClear
              value={orderListSearchValue}
              onChange={(event) => setOrderListSearchValue(event.target.value)}
              size="large"
              suffix={<SearchOutlined className="text-lg !text-blue-600" />}
              className="w-full sm:!w-96 !rounded"
            />
            <Table<FrontsiteOrder>
              columns={orderListColumns}
              dataSource={filteredOrderList}
              rowKey={(record, index) =>
                `order-list-${record.order_id ?? record.id ?? record.so_number ?? index}`
              }
              pagination={{
                current: orderListPagination.current,
                pageSize: orderListPagination.pageSize,
                total: filteredOrderList.length,
                showSizeChanger: true,
                pageSizeOptions: ['10', '25', '50'],
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} entries`,
                showQuickJumper: true,
                className: '!px-4'
              }}
              onChange={handleOrderListTableChange}
              size="small"
              scroll={{ x: 'max-content' }}
              className='border'
            />
            
            {/* unique company view > view drawer > view drawer */}
            <Drawer
              title={
                orderListDrawerState.selectedOrder
                  ? orderListDrawerState.selectedOrder.so_number ||
                    `Order #${orderListDrawerState.selectedOrder.order_id || orderListDrawerState.selectedOrder.id}`
                  : 'Order Details'
              }
              placement="right"
              width={950}
              open={Boolean(orderListDrawerState.selectedOrder)}
              onClose={() =>
                setOrderListDrawerState((prev) => ({
                  ...prev,
                  selectedOrder: null,
                }))
              }
              destroyOnClose
              push={false}
              getContainer={false}
              style={{ position: 'absolute', top: 0, right: 0 }}
            >
              <FrontsiteOrderDetails
                order={orderListDrawerState.selectedOrder ?? null}
                formatCurrency={formatCurrency}
                formatExactCurrency={formatExactCurrency}
              />
            </Drawer>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default FrontsiteDrawerContent;
